import { useState } from "react";
import classNames from "classnames";
import { Field, Form, Formik } from "formik";

import {
  AscModalEditProfilePropsI,
  MyFormValues,
} from "./AscModalEditProfile.type";
import AscSelectInput from "../AscInputFields/AscSelectInput/AscSelectInput";
import AscFilesUploader from "../AscFilesUploader/AscFilesUploader";
import { AdminEditPictureIcon, AdminImageIcon } from "../AscIcons";
import { appConfig } from "@ascendnow/shared";

import styles from "./AscModalEditProfile.module.scss";
import AscButton from "../AscButton/AscButton";
import AscModal from "../AscModal/AscModal";
import { TEAM_MEMBER_ROLES } from "../AscModalNewMember/AscModalNewMember";

function AscModalEditProfile({
  visible,
  setVisible,
  header,
  image,
  name,
  email,
  role,
  isProfile = false,
  onSubmit,
}: AscModalEditProfilePropsI) {
  const [updoadModal, setIsModalOpen] = useState(false);

  const initialValues: MyFormValues = {
    role: role || "",
    newEmail: email || "",
    name: name || "",
    image: image || null,
  };

  function closeModal() {
    setVisible(false);
  }

  function validateEmail(value: any) {
    let error;
    if (!value) {
      error = "Required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
      error = "Invalid email address";
    }
    return error;
  }

  return (
    <AscModal
      visible={visible}
      setVisible={setVisible}
      modalClassName={styles.modalClassName}
      withBackground={false}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={(values: MyFormValues) => {
          onSubmit({
            ...(values.name !== name ? { name: values.name } : {}),
            ...(values.newEmail !== email ? { newEmail: values.newEmail } : {}),
            ...(values.role !== role ? { role: values.role } : {}),
            ...(typeof values.image !== "string" && values.image !== image
              ? { image: values.image }
              : {}),
          });
        }}
      >
        {({ errors, touched, setFieldValue, values, isValid, dirty }) => (
          <Form className={styles.ground} data-testid="asc-modal-edit-profile">
            <span className={styles.header}>{header}</span>
            <div className={styles.imageContainer}>
              <div className={styles.imageGround}>
                {values.image ? (
                  <img
                    src={
                      typeof values.image === "string"
                        ? values.image
                        : URL.createObjectURL(values.image)
                    }
                    alt="profile-avatar"
                    className={styles.image}
                  />
                ) : (
                  <AdminImageIcon className={styles.picture} />
                )}
                <div
                  className={styles.editPictureBlock}
                  onClick={() => setIsModalOpen(true)}
                >
                  <AdminEditPictureIcon className={styles.editPicture} />
                </div>
              </div>
            </div>

            <div className={styles.inputGround}>
              {!isProfile && (
                <div className={styles.inputField}>
                  <label htmlFor="role" className={styles.inputLabel}>
                    Position{" "}
                    <span className={styles.inputLabelRequired}>*</span>
                  </label>
                  <div className={styles.circle} />
                  <AscSelectInput
                    className={classNames(styles.input, styles.inputPosition, {
                      [styles.errorInput]:
                        Boolean(errors?.role) && touched?.role,
                    })}
                    id="role"
                    name="role"
                    fullWidth={true}
                    placeholder="Select a position..."
                    onChange={(val) => setFieldValue("role", val)}
                    value={values.role}
                  >
                    {TEAM_MEMBER_ROLES?.map((role) => (
                      <AscSelectInput.Option
                        key={role.id}
                        value={role.id}
                        label={role.title}
                      />
                    ))}
                  </AscSelectInput>
                  {errors?.role && touched?.role && (
                    <div className={styles.fieldError}>{errors?.role}</div>
                  )}
                </div>
              )}

              <div className={styles.inputField}>
                <label htmlFor="name" className={styles.inputLabel}>
                  Name
                </label>
                <Field
                  id="name"
                  className={classNames(styles.input, {
                    [styles.errorInput]: Boolean(errors?.name) && touched?.name,
                  })}
                  name="name"
                />
              </div>

              {(!isProfile || appConfig.role.TEACHER !== role) && (
                <div className={styles.inputField}>
                  <label htmlFor="newEmail" className={styles.inputLabel}>
                    Email
                  </label>
                  <Field
                    className={classNames(styles.input, {
                      [styles.errorInput]:
                        Boolean(errors?.newEmail) && touched?.newEmail,
                    })}
                    id="newEmail"
                    name="newEmail"
                    placeholder="jamesjones@gmail.com"
                    validate={validateEmail}
                  />
                </div>
              )}
            </div>

            <div className={styles.buttonGround}>
              <AscButton
                className={styles.button}
                variant="secondary"
                onClick={closeModal}
              >
                Cancel
              </AscButton>
              <AscButton className={styles.button} type="submit">
                Save
              </AscButton>
            </div>

            {updoadModal && (
              <AscModal
                visible={updoadModal}
                onClose={() => setIsModalOpen(false)}
                innerContentClassName={styles.modalContent}
              >
                <AscFilesUploader
                  name="image"
                  type="image"
                  accept={{
                    "image/*": [".jpeg", ".png", ".jpg"],
                  }}
                  multiple={false}
                  onDrop={(acceptedFiles: File[]) => {
                    setFieldValue("image", acceptedFiles[0]);
                    setIsModalOpen(false);
                  }}
                  srcFile=""
                  className={styles.image}
                />
              </AscModal>
            )}
          </Form>
        )}
      </Formik>
    </AscModal>
  );
}

export default AscModalEditProfile;
